import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  Activity,
  AssignmentTimeRange,
  COMPACT_ROW_HEIGHT,
  Shift,
  UserTimelinesModal,
} from '@wilson/interfaces';
import {
  GanttDimensionDataSourceService,
  ValidationDataSourceService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { TimelineModalFilterService } from '@wilson/shift-timeline/core';
import { CreateNewViaDragAndDropService } from '@wilson/shift-timeline/services';
import {
  MODAL_VIEW,
  SetTimelineModalFilterUsers,
  ShiftAssignmentModalState,
  TimelineFetchShiftAssignments,
  TimelineModalFetchUsers,
  TimelineModalFilterState,
  TimelineModalSetDaysRange,
  TimelineModalSetTimeframeWidth,
  ToggleFilterLoading,
} from '@wilson/shift-timeline/state';
import { add, eachDayOfInterval, endOfDay, startOfDay, sub } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom, map, Observable, tap } from 'rxjs';
import { FindStaffDataService } from '../services/find-staff-data.service';
import { FindStaffGanttChartService } from '../services/find-staff-gantt-chart.service';
import { FindStaffPopupHelperService } from '../services/find-staff-popup-helper.service';
import { FindStaffValidationDataSourceService } from '../services/find-staff-validation-data-source.service';
import { GanttModalBaseService } from '../services/gantt-modal-base.service';
import { TimelineModalDimensionDataSourceService } from '../services/timeline-modal-dimension-data-source.service';

export enum AssignmentModalMode {
  ShiftAssignment = 'ShiftAssignment',
  ActivitiesAssignment = 'ActivitiesAssignment',
}

@Component({
  selector: 'wilson-find-shift-staff-popup',
  templateUrl: './find-shift-staff-popup.component.html',
  styleUrls: ['./find-shift-staff-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TimelineModalFilterService,
    CreateNewViaDragAndDropService,
    FindStaffGanttChartService,
    FindStaffDataService,
    {
      provide: ValidationDataSourceService,
      useClass: FindStaffValidationDataSourceService,
    },
    {
      provide: GanttDimensionDataSourceService,
      useClass: TimelineModalDimensionDataSourceService,
    },
  ],
  standalone: false,
})
export class FindShiftStaffPopupComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly toastr = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private ganttModalBaseService = inject(GanttModalBaseService);
  private findStaffPopupHelperService = inject(FindStaffPopupHelperService);
  private findStaffGanttChartService = inject(FindStaffGanttChartService);
  private findStaffDataService = inject(FindStaffDataService);

  userTimelinesInfo$!: Observable<UserTimelinesModal[]>;
  public isFetchedShiftAssignmentSuccessful$!: Observable<boolean>;

  @ViewChild('findStaffWrapper', { static: true })
  findStaffWrapper!: ElementRef<HTMLDivElement>;

  async ngOnInit(): Promise<void> {
    this.store.dispatch(new TimelineModalSetTimeframeWidth(MODAL_VIEW.SHIFT));
    this.findStaffGanttChartService.setViewAndHoursRange(MODAL_VIEW.SHIFT);
    this.findStaffGanttChartService.setDefaultRowHeight(COMPACT_ROW_HEIGHT);
    this.findStaffGanttChartService.setHolidaysRegion(
      this.store.selectSnapshot(AuthState.publicHolidaysRegion),
    );
    await firstValueFrom(
      this.findStaffDataService.timelineFetchUsersOvertimes(),
    );

    this.ganttModalBaseService.findStaffWrapper = this.findStaffWrapper;
    this.ganttModalBaseService.isAssignStaffModal = true;
    this.isFetchedShiftAssignmentSuccessful$ = this.store.select(
      ShiftAssignmentModalState.isFetchedShiftAssignmentSuccessful,
    );

    await this.loadUsersForShiftAssignment();
  }

  private async loadUsersForShiftAssignment(numberOfDays = 3): Promise<void> {
    const shiftToAssign = this.store.selectSnapshot(
      ShiftAssignmentModalState.shiftToAssign,
    ) as Shift & {
      activities: Activity[];
    };

    const assignmentTimeRange: AssignmentTimeRange = {
      start: shiftToAssign.activities[0].startDatetime,
      end: shiftToAssign.activities[shiftToAssign.activities.length - 1]
        .endDatetime,
    };

    this.filterUsers(assignmentTimeRange);

    if (shiftToAssign?.id) {
      const startDate = sub(new Date(shiftToAssign.startDate), {
        days: numberOfDays,
      });
      const endDate = add(new Date(shiftToAssign.startDate), {
        days: numberOfDays,
      });

      await this.setTimelineDataAndWindowWidthForShiftAssignment(
        startDate,
        endDate,
        shiftToAssign,
        assignmentTimeRange,
      );
    }
  }

  private filterUsers(assignmentTimeRange: AssignmentTimeRange): void {
    this.userTimelinesInfo$ = this.store
      .select(
        TimelineModalFilterState.shiftAssignmentModalFilteredUsers(
          assignmentTimeRange,
        ),
      )
      .pipe(
        map((filteredUsers) => {
          return this.findStaffPopupHelperService.sortStaffSearchResults(
            filteredUsers,
          );
        }),
        tap(() => {
          setTimeout(() => {
            this.store.dispatch(new ToggleFilterLoading(false));
          });
        }),
      );
  }

  private async setTimelineDataAndWindowWidthForShiftAssignment(
    startDate: Date,
    endDate: Date,
    shiftToAssign: Shift & { activities: Activity[] },
    assignmentTimeRange: AssignmentTimeRange,
  ): Promise<void> {
    const timeframe = {
      start: startOfDay(startDate),
      end: endOfDay(endDate),
    };
    this.store.dispatch(new TimelineModalSetDaysRange(timeframe));

    const fetchUserActions = this.store
      .dispatch([new TimelineModalFetchUsers(timeframe, assignmentTimeRange)])
      .pipe(
        tap(() => {
          this.store.dispatch(
            new SetTimelineModalFilterUsers(
              this.store.selectSnapshot(
                ShiftAssignmentModalState.timelineModalUsers,
              ),
            ),
          );
          this.ganttModalBaseService.updateModalWidth();
        }),
      );

    const fetchShiftAssignmentsAction = this.store.dispatch(
      new TimelineFetchShiftAssignments(shiftToAssign.id as string),
    );

    try {
      firstValueFrom(fetchUserActions);
      firstValueFrom(fetchShiftAssignmentsAction);
    } catch {
      this.toastr.error(this.translate.instant('general.something_went_wrong'));
    }
  }
}
